import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { uiLocale, monthLong, monthShort } from '../lib/dateFormat'
import { Icon } from './Icon'
import { resolveSelectPopover, type SelectPopoverPos } from './WaSelect'

const MONTH_INDEXES = Array.from({ length: 12 }, (_, i) => i)
const POPOVER_WIDTH = 264

export interface MonthYearPickerProps {
  /** Currently displayed year. */
  year: number
  /** Currently displayed month, 0–11. */
  month: number
  /** Navigate directly to the chosen month (0–11) and year. */
  onPick: (year: number, month: number) => void
}

/**
 * Tappable "Month Year" heading that opens an anchored popover for jumping
 * straight to any month — with a year stepper so past/future seasons are
 * reachable without clicking the month arrows repeatedly. Reflects the
 * currently displayed month/year each time it opens. Anchoring (downward,
 * flip-up) reuses WaSelect's tested resolver.
 */
export function MonthYearPicker({ year, month, onPick }: MonthYearPickerProps) {
  const { t, i18n } = useTranslation('hunts')
  const locale = uiLocale(i18n.language)
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(year)
  const [pos, setPos] = useState<SelectPopoverPos | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const toggle = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      setPos(resolveSelectPopover({ top: r.top, bottom: r.bottom, left: r.left, width: POPOVER_WIDTH }, window.innerHeight, window.innerWidth))
      setViewYear(year) // reflect the displayed year on open
    }
    setOpen((v) => !v)
  }

  const popStyle: CSSProperties = pos
    ? {
        position: 'fixed',
        left: pos.left,
        width: POPOVER_WIDTH,
        ...(pos.placement === 'below' ? { top: pos.top } : { bottom: pos.bottom }),
      }
    : { position: 'fixed' }

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: COLORS.onSurface,
          fontFamily: COLORS.fontDisplay,
          fontSize: 22,
          fontWeight: 500,
        }}
      >
        {monthLong(locale, month)} {year}
        <Icon name="chevron-down" size={16} color={COLORS.outline} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1200 }} onClick={() => setOpen(false)} />
          <div
            role="dialog"
            aria-label={t('calendar.pickMonthYear')}
            onClick={(e) => e.stopPropagation()}
            style={{
              ...popStyle,
              zIndex: 1201,
              padding: 12,
              background: COLORS.surfaceContainerLowest,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
            }}
          >
            {/* Year stepper */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <button
                type="button"
                className="wh-btn-icon"
                aria-label={t('calendar.previousYear')}
                onClick={() => setViewYear((y) => y - 1)}
              >
                <Icon name="chevron-left" size={18} />
              </button>
              <span style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 700 }}>{viewYear}</span>
              <button
                type="button"
                className="wh-btn-icon"
                aria-label={t('calendar.nextYear')}
                onClick={() => setViewYear((y) => y + 1)}
              >
                <Icon name="chevron-right" size={18} />
              </button>
            </div>

            {/* Month grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {MONTH_INDEXES.map((i) => {
                const label = monthShort(locale, i)
                const active = viewYear === year && i === month
                return (
                  <button
                    key={i}
                    type="button"
                    aria-label={`${monthLong(locale, i)} ${viewYear}`}
                    aria-pressed={active}
                    onClick={() => {
                      onPick(viewYear, i)
                      setOpen(false)
                    }}
                    style={{
                      padding: '8px 0',
                      borderRadius: 7,
                      border: `1.5px solid ${active ? COLORS.primary : 'transparent'}`,
                      background: active ? COLORS.primaryContainer : 'transparent',
                      color: active ? COLORS.primary : COLORS.onSurface,
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
